/**
 * SPEAKON — floating launcher.
 *
 * A floating action button, fixed bottom-right, that opens the AI assistant as a
 * chat overlay. Available on every authenticated page so a merchant never has to
 * leave what they are doing to ask the agent something.
 *
 * Mounted once inside PrivateLayout; hidden on the dedicated /ai-assistant page.
 *
 * Closing the overlay NEVER cancels in-flight work. Agent runs and image
 * generation are durable server-side tasks, so they keep going while the modal is
 * closed. When one finishes, the merchant gets a clickable notification that
 * reopens the assistant on the same conversation.
 *
 * Note: the overlay unmounts instantly on close rather than animating out —
 * framer-motion's AnimatePresence exit does not complete reliably under React
 * StrictMode and would leave an invisible overlay blocking page clicks.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { Sparkles, Minimize2, Bell, Loader2 } from "lucide-react";
import GestockAiChat from "../views/AiAssistant/GestockAiChat";

interface AiNotification {
  id: string;
  title: string;
  message: string;
  createdAt: number;
  read: boolean;
}

/** Everything a background job (agent run, image generation, confirm) reports. */
interface BackgroundResult {
  title: string;
  message: string;
}

export default function GestockAiWidget() {
  const scope = useSelector((state: any) => {
    const data = state.UserReducer?.userdata;
    return `${data?.user?._id}:${data?.store?._id}`;
  });
  return <ScopedGestockAiWidget key={scope} />;
}

function ScopedGestockAiWidget() {
  const activeRef = useRef(true);
  const notificationToasts = useRef<string[]>([]);
  useEffect(() => {
    const ownedToasts = notificationToasts.current;
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      ownedToasts.forEach((id) => toast.dismiss(id));
    };
  }, []);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AiNotification[]>([]);
  const [pending, setPending] = useState(false);
  const location = useLocation();
  const openRef = useRef(open);
  openRef.current = open;

  // The full-page assistant already covers this route.
  const isDedicatedPage = location.pathname === "/ai-assistant";

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  // Opening the modal marks everything as read.
  useEffect(() => {
    if (!open) return;
    setNotifications((current) =>
      current.some((item) => !item.read) ? current.map((item) => ({ ...item, read: true })) : current
    );
  }, [open]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications]
  );

  /**
   * A long operation finished. The chat thread always renders it (it is visible
   * the moment the modal reopens); while the modal is CLOSED we also surface a
   * clickable toast so the merchant is not left waiting in silence.
   */
  const handleBackgroundResult = useCallback((result: BackgroundResult) => {
    if (!activeRef.current) return;
    const item: AiNotification = {
      id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: result.title,
      message: result.message,
      createdAt: Date.now(),
      read: openRef.current,
    };

    setNotifications((current) => [item, ...current].slice(0, 30));

    if (openRef.current) return;

    const toastId = toast.custom(
      (t) => (
        <button
          type="button"
          onClick={() => {
            toast.dismiss(t.id);
            setNotifications((current) => current.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
            setOpen(true);
          }}
          className="pointer-events-auto flex w-[min(92vw,360px)] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-[0_18px_44px_rgba(15,23,42,0.22)]"
        >
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
            <Sparkles size={17} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-slate-900">{result.title}</span>
            <span className="mt-0.5 block line-clamp-2 text-xs text-slate-600">{result.message}</span>
            <span className="mt-1 block text-[11px] font-semibold text-cyan-700">
              Ouvrir l'assistant →
            </span>
          </span>
        </button>
      ),
      { duration: 12000, position: "bottom-right" }
    );
    notificationToasts.current.push(toastId);
  }, []);

  const handlePendingChange = useCallback((value: boolean) => setPending(value), []);

  if (isDedicatedPage) return null;

  return (
    <>
      {/* ---------------------------------------------------- chat overlay */}
      {open && (
        <div className="fixed inset-0 z-[120]">
          {/* Backdrop: catches outside clicks, dims the page behind */}
          <div
            className="animate-gestockAiBackdropIn absolute inset-0 bg-slate-950/25 backdrop-blur-[2px]"
            onClick={close}
            aria-hidden="true"
          />

          {/* Panel: bottom-right on desktop, bottom sheet on mobile */}
          <div
            role="dialog"
            aria-label="Assistant SPEAKON"
            className="animate-gestockAiOverlayIn absolute inset-x-3 bottom-24 sm:inset-x-auto sm:right-6 sm:w-[470px]"
          >
            <div
              className="flex h-[min(66vh,640px)] flex-col overflow-hidden sm:h-[min(760px,calc(100vh-7rem))]
                         rounded-2xl border border-slate-200 bg-white shadow-[0_28px_70px_rgba(15,23,42,0.28)]"
            >
              <div className="relative flex-1 min-h-0">
                {/* Close control, top-right of the panel */}
                <div className="absolute right-4 top-[1.15rem] z-10 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-lg p-1.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
                    aria-label="Réduire l'assistant"
                    title="Réduire — le travail en cours continue"
                  >
                    <Minimize2 size={17} />
                  </button>
                </div>

                <GestockAiChat
                  onBackgroundResult={handleBackgroundResult}
                  onPendingChange={handlePendingChange}
                  hideStatusBadge={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --------------------------------------------------- floating button */}
      {!open && (
        <button
          type="button"
          onClick={toggle}
          className="group fixed bottom-6 right-6 z-[70] flex h-14 items-center gap-2.5
                     rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 pl-4 pr-5
                     text-white shadow-[0_14px_34px_rgba(8,145,178,0.45)]
                     transition hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(8,145,178,0.55)]
                     focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2"
          aria-label={
            unreadCount > 0
              ? `Ouvrir SPEAKON (${unreadCount} nouveauté${unreadCount > 1 ? "x" : ""})`
              : "Ouvrir SPEAKON"
          }
          title="SPEAKON"
        >
          {/* Soft attention halo */}
          <span
            className="animate-gestockAiHalo pointer-events-none absolute inset-0 rounded-full bg-cyan-400/40"
            aria-hidden="true"
          />

          {/* Badge: unread responses while the assistant was closed */}
          {unreadCount > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full
                         bg-rose-500 px-1 text-[11px] font-bold text-white ring-2 ring-white"
              aria-hidden="true"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}

          {/* Spinner: something is still running server-side */}
          {pending && unreadCount === 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 items-center justify-center rounded-full
                         bg-slate-900 px-1 text-white ring-2 ring-white"
              title="Traitement en cours…"
            >
              <Loader2 size={11} className="animate-spin" />
            </span>
          )}

          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
            {unreadCount > 0 ? <Bell size={18} /> : <Sparkles size={19} />}
          </span>

          <span className="relative flex flex-col items-start leading-none">
            <span className="text-sm font-semibold tracking-tight">SPEAKON</span>
            <span className="mt-0.5 text-[10px] font-medium text-white/80">
              {unreadCount > 0 ? "Nouveau résultat" : pending ? "En cours…" : "Assistant intelligent"}
            </span>
          </span>
        </button>
      )}
    </>
  );
}